import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import PhoneInputField from 'components/PhoneInputField';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { fetchFromStorage } from 'utils/storage';
import { Validators } from 'utils/validators/Validators';
import { CountryCode, getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { getSaBookingFieldLabel } from './saBookingFieldLabels';

export type SaBookingDetailPayload = {
  booking: Record<string, unknown>;
  bookingLineItems: Record<string, unknown>[];
};

/** Field order for the guest / company accordion (API camelCase). */
export const GUEST_BOOKING_SECTION_FIELDS = [
  'customerFirstName',
  'customerLastName',
  'email',
  'countryCode',
  'phoneNumber',
  'company',
  'companyName',
  'designation',
  'gst',
  'country',
  'state',
  'city',
  'pinCode',
  'address',
] as const;

const GUEST_WIDE_FIELDS = new Set(['address']);

type GeoRow = { id: number; name: string };

type SaGuestPhone = { countryCode: CountryCode | null; value: string };

type SaGuestDetailsPayload = {
  customerFirstName: string;
  customerLastName: string;
  email: string;
  phone: SaGuestPhone;
  companyName: string;
  designation: string;
  gst: string;
  address: string;
  pinCode: string;
  company: boolean;
  countryId: number | null;
  stateId: number | null;
  cityId: number | null;
};

function coerceBooleanDisplay(raw: unknown): boolean {
  if (raw === true) return true;
  if (raw === false) return false;
  if (raw === null || raw === undefined) return false;
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw !== 0;
  }
  return Boolean(raw);
}

function normalizeGeoName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

function bookingToPhoneInput(booking: Record<string, unknown>, defaultCountry: CountryCode): SaGuestPhone {
  const rawCc =
    booking.countryCode == null || booking.countryCode === ''
      ? null
      : String(booking.countryCode).trim().toUpperCase();
  const rawNum = booking.phoneNumber == null ? '' : String(booking.phoneNumber).trim();
  if (!rawNum && !rawCc) {
    return { countryCode: null, value: '' };
  }
  const fallbackCc = (rawCc && /^[A-Z]{2}$/.test(rawCc) ? rawCc : defaultCountry) as CountryCode;
  try {
    if (rawNum.startsWith('+')) {
      const p = parsePhoneNumber(rawNum);
      return { countryCode: (p.country ?? fallbackCc) as CountryCode, value: p.format('E.164') };
    }
    if (rawNum) {
      const p = parsePhoneNumber(rawNum, fallbackCc);
      return { countryCode: (p.country ?? fallbackCc) as CountryCode, value: p.format('E.164') };
    }
  } catch {
    /* fall through */
  }
  if (rawCc && /^[A-Z]{2}$/.test(rawCc) && rawNum) {
    try {
      const dial = getCountryCallingCode(rawCc as CountryCode);
      const digits = rawNum.replace(/\D/g, '');
      return { countryCode: rawCc as CountryCode, value: `+${dial}${digits}` };
    } catch {
      /* fall through */
    }
  }
  return {
    countryCode: rawCc && /^[A-Z]{2}$/.test(rawCc) ? (rawCc as CountryCode) : defaultCountry,
    value: rawNum,
  };
}

function emptyGuestFormFromBooking(booking: Record<string, unknown>, defaultCountry: CountryCode): SaGuestDetailsPayload {
  return {
    customerFirstName: booking.customerFirstName == null ? '' : String(booking.customerFirstName),
    customerLastName: booking.customerLastName == null ? '' : String(booking.customerLastName),
    email: booking.email == null ? '' : String(booking.email),
    phone: bookingToPhoneInput(booking, defaultCountry),
    companyName: booking.companyName == null ? '' : String(booking.companyName),
    designation: booking.designation == null ? '' : String(booking.designation),
    gst: booking.gst == null ? '' : String(booking.gst),
    address: booking.address == null ? '' : String(booking.address),
    pinCode: booking.pinCode == null ? '' : String(booking.pinCode),
    company: coerceBooleanDisplay(booking.company),
    countryId: null,
    stateId: null,
    cityId: null,
  };
}

function trimToNull(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

function guestFormToRequestBody(
  f: SaGuestDetailsPayload,
  geo: { countries: GeoRow[]; states: GeoRow[]; cities: GeoRow[] },
) {
  const phoneNumber = trimToNull(f.phone.value ?? '');
  const countryCode =
    phoneNumber && f.phone.countryCode ? String(f.phone.countryCode) : null;
  const countryName =
    f.countryId != null
      ? trimToNull(geo.countries.find(c => c.id === f.countryId)?.name ?? '')
      : null;
  const stateName =
    f.stateId != null ? trimToNull(geo.states.find(s => s.id === f.stateId)?.name ?? '') : null;
  const cityName =
    f.cityId != null ? trimToNull(geo.cities.find(c => c.id === f.cityId)?.name ?? '') : null;
  return {
    customerFirstName: f.customerFirstName.trim(),
    customerLastName: trimToNull(f.customerLastName),
    email: trimToNull(f.email),
    countryCode,
    phoneNumber,
    companyName: f.company ? trimToNull(f.companyName) : null,
    designation: f.company ? trimToNull(f.designation) : null,
    gst: f.company ? trimToNull(f.gst) : null,
    address: trimToNull(f.address),
    pinCode: trimToNull(f.pinCode),
    company: f.company,
    country: countryName,
    state: stateName,
    city: cityName,
  };
}

const EditableBooleanGuestField: FC<{
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}> = ({ label, checked, onChange, disabled }) => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      px: 1.25,
      py: 0.75,
      minHeight: 40,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 0.75,
      bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
    }}>
    <Switch
      checked={checked}
      disabled={disabled}
      size="small"
      sx={{ ml: -0.75 }}
      onChange={(_, v) => onChange(v)}
    />
    <Typography variant="body2" color="text.primary" component="span" sx={{ lineHeight: 1.35, flex: 1 }}>
      {label}
    </Typography>
  </Box>
);

export type SuperAdminBookingGuestDetailsSectionProps = {
  booking: Record<string, unknown> | null;
  orderReference: string;
  sectionTitle: string;
  rowCount: number;
  defaultExpanded?: boolean;
  onUpdated: (detail: SaBookingDetailPayload) => void;
};

export const SuperAdminBookingGuestDetailsSection: FC<SuperAdminBookingGuestDetailsSectionProps> = ({
  booking,
  orderReference,
  sectionTitle,
  rowCount,
  defaultExpanded = true,
  onUpdated,
}) => {
  const [guestForm, setGuestForm] = useState<SaGuestDetailsPayload | null>(null);
  const [geoOptions, setGeoOptions] = useState<{ countries: GeoRow[]; states: GeoRow[]; cities: GeoRow[] }>({
    countries: [],
    states: [],
    cities: [],
  });
  const [guestSaving, setGuestSaving] = useState(false);
  const [guestSaveError, setGuestSaveError] = useState<string | null>(null);

  const defaultCountry = useMemo(
    () => (fetchFromStorage('defaultCountryCode') || 'IN') as CountryCode,
    [],
  );

  useEffect(() => {
    setGuestSaveError(null);
    let cancelled = false;

    async function syncGuestAndGeo() {
      if (!booking) {
        setGuestForm(null);
        setGeoOptions({ countries: [], states: [], cities: [] });
        return;
      }
      const b = booking;
      const base = emptyGuestFormFromBooking(b, defaultCountry);
      try {
        const { data: countries } = await axiosInstance.get<GeoRow[]>(URLS.countries);
        if (cancelled) {
          return;
        }
        const countryName = b.country == null ? '' : String(b.country).trim();
        const stateName = b.state == null ? '' : String(b.state).trim();
        const cityName = b.city == null ? '' : String(b.city).trim();

        let countryId: number | null = null;
        let stateId: number | null = null;
        let cityId: number | null = null;
        let states: GeoRow[] = [];
        let cities: GeoRow[] = [];

        if (countryName) {
          const cMatch = countries.find(c => normalizeGeoName(c.name) === normalizeGeoName(countryName));
          if (cMatch) {
            countryId = cMatch.id;
            const { data: st } = await axiosInstance.get<GeoRow[]>(URLS.states(cMatch.id));
            if (cancelled) {
              return;
            }
            states = st;
            if (stateName) {
              const sMatch = st.find(s => normalizeGeoName(s.name) === normalizeGeoName(stateName));
              if (sMatch) {
                stateId = sMatch.id;
                const { data: ct } = await axiosInstance.get<GeoRow[]>(URLS.cities(sMatch.id));
                if (cancelled) {
                  return;
                }
                cities = ct;
                if (cityName) {
                  const ciMatch = ct.find(x => normalizeGeoName(x.name) === normalizeGeoName(cityName));
                  cityId = ciMatch?.id ?? null;
                }
              }
            }
          }
        }

        setGeoOptions({ countries, states, cities });
        setGuestForm({ ...base, countryId, stateId, cityId });
      } catch {
        if (!cancelled) {
          setGeoOptions({ countries: [], states: [], cities: [] });
          setGuestForm({ ...base, countryId: null, stateId: null, cityId: null });
        }
      }
    }

    void syncGuestAndGeo();
    return () => {
      cancelled = true;
    };
  }, [booking, defaultCountry]);

  const handleSave = useCallback(async () => {
    const ref = orderReference.trim();
    if (!guestForm || !ref) {
      return;
    }
    if (!guestForm.customerFirstName.trim()) {
      setGuestSaveError(strings.requiredText);
      return;
    }
    if (guestForm.countryId != null && (guestForm.stateId == null || guestForm.cityId == null)) {
      setGuestSaveError(strings.sa_booking_lookup_geo_incomplete);
      return;
    }
    const geoBody = guestFormToRequestBody(guestForm, geoOptions);
    if (
      guestForm.countryId != null &&
      (!geoBody.country || !geoBody.state || !geoBody.city)
    ) {
      setGuestSaveError(strings.sa_booking_lookup_geo_incomplete);
      return;
    }
    setGuestSaving(true);
    setGuestSaveError(null);
    try {
      const { data: res } = await axiosInstance.patch<SaBookingDetailPayload>(
        URLS.superAdminBookingGuestDetails(ref),
        geoBody,
      );
      onUpdated(res);
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { message?: string; error_description?: string } };
        message?: string;
      };
      const msg =
        err.response?.data?.message ?? err.response?.data?.error_description ?? err.message ?? 'Request failed';
      setGuestSaveError(msg);
    } finally {
      setGuestSaving(false);
    }
  }, [guestForm, geoOptions, orderReference, onUpdated]);

  if (!guestForm) {
    return null;
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        overflow: 'hidden',
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle1" component="div" fontWeight={600}>
          {sectionTitle}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({rowCount})
          </Typography>
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 2.5 }}>
        {guestSaveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGuestSaveError(null)}>
            {guestSaveError}
          </Alert>
        )}
        <Grid container spacing={2}>
          {GUEST_BOOKING_SECTION_FIELDS.map(field => {
            const label = getSaBookingFieldLabel(field);
            const wide = GUEST_WIDE_FIELDS.has(field);
            if (field === 'countryCode') {
              return (
                <Grid item xs={12} md={6} lg={4} key="guest-phone-input">
                  <PhoneInputField
                    field="guestPhone"
                    label={strings.customermgmt_text_phone}
                    inputValue={guestForm.phone}
                    required={false}
                    defaultCountry={defaultCountry}
                    disabled={guestSaving}
                    variant="outlined"
                    size="small"
                    validators={[{ check: Validators.validateMobile }]}
                    textChange={v => {
                      setGuestForm(g =>
                        g
                          ? {
                              ...g,
                              phone: {
                                countryCode: v.countryCode,
                                value: v.value ?? '',
                              },
                            }
                          : null,
                      );
                    }}
                  />
                </Grid>
              );
            }
            if (field === 'phoneNumber') {
              return null;
            }
            if (field === 'companyName' || field === 'designation' || field === 'gst') {
              if (!guestForm.company) {
                return null;
              }
            }
            if (field === 'company') {
              return (
                <Grid item xs={12} md={6} lg={4} key={field}>
                  <EditableBooleanGuestField
                    label={label}
                    checked={guestForm.company}
                    disabled={guestSaving}
                    onChange={next =>
                      setGuestForm(g =>
                        g
                          ? {
                              ...g,
                              company: next,
                              ...(next
                                ? {}
                                : { companyName: '', designation: '', gst: '' }),
                            }
                          : null,
                      )
                    }
                  />
                </Grid>
              );
            }
            if (field === 'country' || field === 'state' || field === 'city') {
              const isCountry = field === 'country';
              const isState = field === 'state';
              const options = isCountry
                ? geoOptions.countries
                : isState
                  ? geoOptions.states
                  : geoOptions.cities;
              const selValue = isCountry
                ? guestForm.countryId
                : isState
                  ? guestForm.stateId
                  : guestForm.cityId;
              const disabledSelect =
                guestSaving ||
                (isState && guestForm.countryId == null) ||
                (field === 'city' && guestForm.stateId == null);
              return (
                <Grid item xs={12} md={6} lg={4} key={field}>
                  <TextField
                    select
                    label={label}
                    value={selValue != null ? String(selValue) : ''}
                    onChange={e => {
                      const raw = e.target.value;
                      const id = raw === '' ? null : Number(raw);
                      if (isCountry) {
                        setGuestForm(g => (g ? { ...g, countryId: id, stateId: null, cityId: null } : null));
                        if (id != null) {
                          void axiosInstance
                            .get<GeoRow[]>(URLS.states(id))
                            .then(({ data: st }) => {
                              setGeoOptions(o => ({ ...o, states: st, cities: [] }));
                            });
                        } else {
                          setGeoOptions(o => ({ ...o, states: [], cities: [] }));
                        }
                      } else if (isState) {
                        setGuestForm(g => (g ? { ...g, stateId: id, cityId: null } : null));
                        if (id != null) {
                          void axiosInstance
                            .get<GeoRow[]>(URLS.cities(id))
                            .then(({ data: ct }) => {
                              setGeoOptions(o => ({ ...o, cities: ct }));
                            });
                        } else {
                          setGeoOptions(o => ({ ...o, cities: [] }));
                        }
                      } else {
                        setGuestForm(g => (g ? { ...g, cityId: id } : null));
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={disabledSelect}
                    SelectProps={{ displayEmpty: true }}>
                    <MenuItem value="">{strings.sa_booking_lookup_geo_none}</MenuItem>
                    {options.map(opt => (
                      <MenuItem key={opt.id} value={String(opt.id)}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              );
            }
            const multiline = field === 'address';
            const value = String(guestForm[field as keyof SaGuestDetailsPayload] ?? '');
            return (
              <Grid item xs={12} md={wide ? 12 : 6} lg={wide ? 12 : 4} key={field}>
                <TextField
                  label={label}
                  value={value}
                  onChange={e =>
                    setGuestForm(g => (g ? { ...g, [field]: e.target.value } as SaGuestDetailsPayload : null))
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled={guestSaving}
                  multiline={multiline}
                  minRows={multiline ? 2 : 1}
                  maxRows={multiline ? 12 : 1}
                  type={field === 'email' ? 'email' : 'text'}
                />
              </Grid>
            );
          })}
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 2,
            mt: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}>
          <Button
            variant="contained"
            size="small"
            startIcon={
              guestSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
            }
            onClick={() => void handleSave()}
            disabled={guestSaving || !guestForm.customerFirstName?.trim()}>
            {strings.sa_booking_lookup_save}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
